import { omit } from 'util/objects'
import storage from 'util/storage'

import { statusStates } from 'constants/status_states'
import {
  BOOTSTRAP_WIDGETS,
  FETCH_WIDGETS_REQUEST,
  FETCH_WIDGETS_SUCCESS,
  FETCH_WIDGETS_FAILURE,
  FETCH_WIDGET_SETTINGS_REQUEST,
  FETCH_WIDGET_SETTINGS_SUCCESS,
  FETCH_WIDGET_SETTINGS_FAILURE,
  FETCH_WIDGET_SNIPPET_REQUEST,
  FETCH_WIDGET_SNIPPET_SUCCESS,
  FETCH_WIDGET_SNIPPET_FAILURE,
  UPDATE_WIDGET_SETTINGS_REQUEST,
  UPDATE_WIDGET_SETTINGS_SUCCESS,
  UPDATE_WIDGET_SETTINGS_FAILURE,
  PUBLISH_WIDGET_SETTINGS_REQUEST,
  PUBLISH_WIDGET_SETTINGS_SUCCESS,
  PUBLISH_WIDGET_SETTINGS_FAILURE,
  REVERT_WIDGET_SETTINGS_REQUEST,
  REVERT_WIDGET_SETTINGS_SUCCESS,
  REVERT_WIDGET_SETTINGS_FAILURE,
  CREATE_WIDGET_REQUEST,
  CREATE_WIDGET_SUCCESS,
  CREATE_WIDGET_FAILURE,
  UPDATE_WIDGET_REQUEST,
  UPDATE_WIDGET_SUCCESS,
  UPDATE_WIDGET_FAILURE,
  DELETE_WIDGET_REQUEST,
  DELETE_WIDGET_SUCCESS,
  DELETE_WIDGET_FAILURE,
  UPDATE_WIDGET_PREVIEW_MESSAGE,
  CREATE_DRAFT_WIDGET_REQUEST,
  CREATE_DRAFT_WIDGET_SUCCESS,
  UPDATE_DRAFT_WIDGET,
  DESTROY_DRAFT_WIDGET,
} from './types'

export const defaultSettings = {
  enabled: true,
  'contact.enabled': undefined, // HACK (jscheel): Use undefined to make sure we don't override in demo settings selector
  'contact.id': undefined,
  icon: 'chat',
  iconDisplay: 'both',
  iconStyle: 'outline',
  iconText: 'Need a hand?',
  'kb.enabled': undefined, // HACK (jscheel): Use undefined to make sure we don't override in demo settings selector
  'kb.id': undefined,
  primaryColor: '#6187e0',
  startScreen: undefined, // HACK (jscheel): Use undefined to make sure we don't override in demo settings selector
}

const draftKey = 'groove-draftWidget'

const defaultState = {
  byId: {},
  ids: [],
  isLoading: false,
  isSaving: false,
  bootstrapStatus: statusStates.IDLE,
  settings: {},
  snippet: '',
  error: null,
  isCreatingDraftWidget: false,
  draftWidget: JSON.parse(sessionStorage.getItem(draftKey)),
  recentlyCreatedWidgetId: null, // NOTE (jscheel): We keep this ref so we can load the installation
}

const reducers = {}

reducers[BOOTSTRAP_WIDGETS] = state => {
  return {
    ...state,
    bootstrapStatus: statusStates.PENDING,
  }
}

reducers[FETCH_WIDGETS_REQUEST] = state => {
  return {
    ...state,
    isLoading: true,
  }
}

reducers[FETCH_WIDGETS_SUCCESS] = (state, action) => {
  const {
    payload: { widgets: data },
  } = action

  const newById = {}
  const newIds = []

  data.forEach(widget => {
    newById[widget.id] = { ...state.byId[widget.id], ...widget }
    newIds.push(widget.id)
  })

  return {
    ...state,
    byId: { ...state.byId, ...newById },
    ids: newIds,
    isLoading: false,
    bootstrapStatus: statusStates.SUCCESS,
  }
}

reducers[FETCH_WIDGETS_FAILURE] = (state, action) => {
  const { payload: err } = action.data

  return {
    ...state,
    isLoading: false,
    error: err,
  }
}

reducers[FETCH_WIDGET_SETTINGS_REQUEST] = state => {
  return {
    ...state,
    isLoading: true,
  }
}

reducers[FETCH_WIDGET_SETTINGS_SUCCESS] = (state, action) => {
  const { widgetId, data } = action

  return {
    ...state,
    settings: {
      ...state.settings,
      [widgetId]: omit(['primaryColorRgb'], data),
    },
    isLoading: false,
  }
}

reducers[FETCH_WIDGET_SETTINGS_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isLoading: false,
    error: err,
  }
}

reducers[FETCH_WIDGET_SNIPPET_REQUEST] = state => {
  return {
    ...state,
    isLoading: true,
  }
}

reducers[FETCH_WIDGET_SNIPPET_SUCCESS] = (state, action) => {
  const { data } = action

  return {
    ...state,
    snippet: data,
    isLoading: false,
  }
}

reducers[FETCH_WIDGET_SNIPPET_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isLoading: false,
    error: err,
  }
}

reducers[UPDATE_WIDGET_SETTINGS_REQUEST] = (state, action) => {
  const { widgetId, data } = action

  return {
    ...state,
    settings: {
      ...state.settings,
      [widgetId]: {
        ...data,
        published: false,
      },
    },
    isSaving: true,
  }
}

reducers[UPDATE_WIDGET_SETTINGS_SUCCESS] = state => {
  // Dont update the local settings state after the server
  // responds. This causes the ui to flicker when multiple
  // changes are made in quick succession. It also causes
  // issues when the save operations return out of order
  // due to fluctuating reserver response times
  return {
    ...state,
    isSaving: false,
    error: null,
  }
}

reducers[UPDATE_WIDGET_SETTINGS_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: err,
  }
}

reducers[PUBLISH_WIDGET_SETTINGS_REQUEST] = state => {
  return {
    ...state,
    isSaving: true,
  }
}

reducers[PUBLISH_WIDGET_SETTINGS_SUCCESS] = (state, action) => {
  const { widgetId, data } = action

  return {
    ...state,
    settings: {
      ...state.settings,
      [widgetId]: omit(['primaryColorRgb'], data),
    },
    byId: {
      ...state.byId,
      [widgetId]: {
        ...state.byId[widgetId],
        publishedSettings: omit(['primaryColorRgb'], data),
      },
    },
    isSaving: false,
    error: null,
  }
}

reducers[PUBLISH_WIDGET_SETTINGS_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: err,
  }
}

reducers[REVERT_WIDGET_SETTINGS_REQUEST] = state => {
  return {
    ...state,
    isSaving: true,
  }
}

reducers[REVERT_WIDGET_SETTINGS_SUCCESS] = (state, action) => {
  const { widgetId, data } = action

  return {
    ...state,
    settings: {
      ...state.settings,
      [widgetId]: omit(['primaryColorRgb'], data),
    },
    isSaving: false,
    error: null,
  }
}

reducers[REVERT_WIDGET_SETTINGS_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: err,
  }
}

reducers[CREATE_WIDGET_REQUEST] = state => {
  return {
    ...state,
    isSaving: true,
  }
}

reducers[CREATE_WIDGET_SUCCESS] = (state, action) => {
  const { data } = action
  const newState = {
    ...state,
    ids: [...state.ids, data.id],
    byId: {
      ...state.byId,
      [data.id]: omit(['primaryColorRgb'], data),
    },
    isSaving: false,
    error: null,
    recentlyCreatedWidgetId: data.id,
  }

  return destroyDraftWidget(newState)
}

reducers[CREATE_WIDGET_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: err,
  }
}

reducers[UPDATE_WIDGET_REQUEST] = state => {
  return {
    ...state,
    isSaving: true,
  }
}

reducers[UPDATE_WIDGET_SUCCESS] = (state, action) => {
  const { data } = action

  return {
    ...state,
    byId: {
      ...state.byId,
      [data.id]: data,
    },
    isSaving: false,
    error: null,
  }
}

reducers[UPDATE_WIDGET_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: err,
  }
}

reducers[DELETE_WIDGET_REQUEST] = state => {
  return {
    ...state,
    isSaving: true,
  }
}

reducers[DELETE_WIDGET_SUCCESS] = (state, action) => {
  const { widgetId } = action

  return {
    ...state,
    ids: state.ids.filter(id => id !== widgetId),
    byId: omit([widgetId], state.byId),
    settings: omit([widgetId], state.settings),
    isSaving: false,
    error: null,
  }
}

reducers[DELETE_WIDGET_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: err,
  }
}

reducers[UPDATE_WIDGET_PREVIEW_MESSAGE] = (state, action) => {
  const { hide } = action.data

  storage.set('hideWidgetPreviewMessage', hide)
  return {
    ...state,
    hidePreviewMessage: hide,
  }
}

reducers[CREATE_DRAFT_WIDGET_REQUEST] = state => {
  return {
    ...state,
    isCreatingDraftWidget: true,
  }
}

reducers[CREATE_DRAFT_WIDGET_SUCCESS] = (state, { payload: { settings } }) => {
  const draftWidget = {
    name: '',
    settings: { ...defaultSettings, ...settings },
  }

  sessionStorage.setItem(draftKey, JSON.stringify(draftWidget))

  return {
    ...state,
    isCreatingDraftWidget: false,
    draftWidget,
  }
}

reducers[UPDATE_DRAFT_WIDGET] = (
  state,
  { payload: { widgetFields, settings } }
) => {
  const draftWidget = {
    ...state.draftWidget,
    ...widgetFields,
    settings: {
      ...state.draftWidget.settings,
      ...settings,
    },
  }

  sessionStorage.setItem(draftKey, JSON.stringify(draftWidget))

  return {
    ...state,
    draftWidget,
  }
}

reducers[DESTROY_DRAFT_WIDGET] = state => {
  return destroyDraftWidget(state)
}

function destroyDraftWidget(state) {
  sessionStorage.removeItem(draftKey)
  return {
    ...state,
    draftWidget: null,
  }
}

function maybeDestroyDraftWidget(state, action) {
  // NOTE (jscheel): When the user leaves the create wizard, we no longer want
  // to maintain the draft, and we want to forget about the recentlyCreatedWidgetId.
  const { type } = action
  if (type.endsWith('_PAGE') && !type.startsWith('SETTINGS_WIDGET_CREATE_')) {
    return {
      ...state,
      ...destroyDraftWidget(state),
      recentlyCreatedWidgetId: null,
    }
  }
  return state
}

export default function reducer(state = defaultState, action) {
  const handler = reducers[action.type]
  let newState = state
  if (handler) newState = handler(newState, action)
  return maybeDestroyDraftWidget(newState, action)
}
